import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useFriskOppHypotese, useHypoteseOvergang } from '../api/useHypoteseBehandling.ts';
import { useHypoteseConfig, useHypoteseMedNummer, useMineHypoteser, type Hypothesis, type HypothesisConfig } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Sporingslinje } from '../innboks/Sporingslinje.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { HypotesePille } from '../status/Pille.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel } from '../status/status.ts';
import { Flate, Varsel } from './deler.tsx';
import { Tallrekke } from './EtterValgt.tsx';
import { Logg, sporFor } from './HDetalj.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { fornavn, loefteFor, MAKS_TRENGER_MER, mineIRekkefolge, navnAv, statusNavn, stor, testetPeriode } from './lesning.ts';
import { kortDatoAv, langDatoAv } from './setning.ts';

/**
 * «Hypotesen min» (H2.2; konseptet v0.4 W8, M5a, M5b, M10c — formen; `hypotese-innsending.jsx`
 * SideHypotesen — tilstandene, tastene og ordlyden der konseptet tier). Innsenderens egen side på
 * /h/{nr}, i lesebredde, uten fot: det eneste fylte valget er «Endre hypotesen» i spørsmålskortet,
 * og bare når et spørsmål står åpent (DD-58).
 *
 * Rekkefølgen er konseptets: tilbake og crumben (nummer, sendt, hvem som har den) → tittelen →
 * pillen med koden og løftet ORDRETT → sporingslinjen der datoen er helten når den er lovet →
 * spørsmålskortet etter «Trenger mer» → setningen slik tjeneren bygget den (over 900) → svarene → loggen →
 * «Trekk hypotesen …». Konkludert er svaret selv (M10c): datoen, «Svaret er kommet» og kortet med
 * de tre tallene og «Hva vi lærte».
 *
 * Bare innsenderens: en annens hypotese gir samme «Fant ikke» som en som ikke finnes (K2 —
 * registeret er stedet å lese andres). «Trekk» er en dialog, fordi det rammer en annen person og
 * ikke kan angres (`hypotese-innsending.jsx`, Trekk): dialogen ER bekreftelsen, og kallet går når
 * «Trekk hypotesen» trykkes — intet angre-vindu. Kvitteringen etterpå går av seg selv; en feil
 * blir stående til den lukkes (DD-70).
 */
export function HypotesenMin({ nr }: { nr: number }) {
  const smal = useSmal();
  const naviger = useNavigate();
  const me = useMe();
  const megId = me.data?.id;
  const hypotese = useHypoteseMedNummer(nr);
  const config = useHypoteseConfig();
  const mine = useMineHypoteser();
  const friskOpp = useFriskOppHypotese();
  const [trekker, setTrekker] = useState(false);
  // Rett etter «Send endringen» (Endre.tsx): kvitteringen, som går av seg selv (DD-70).
  const lokasjon = useLocation();
  const endretTekst = (lokasjon.state as { endret?: string } | null)?.endret;
  const [toast, setToast] = useState<ToastInnhold | null>(() => (endretTekst ? { tekst: endretTekst } : null));
  // Tilstanden tømmes, så tilbake og frem i historikken ikke viser kvitteringen på nytt.
  useEffect(() => {
    if (endretTekst) void naviger(lokasjon.pathname, { replace: true, state: null });
  }, [endretTekst, lokasjon.pathname, naviger]);

  const overgang = useHypoteseOvergang();
  const trekk = (h: Hypothesis, note: string) => {
    setToast(null);
    overgang.mutate(
      { id: h.id, overgang: { handling: 'withdraw', body: note ? { note } : {} } },
      {
        onSuccess: () => setToast({ tekst: 'Hypotesen er trukket.' }),
        onError: (e) => {
          if (!erApiFeil(e)) setToast({ tekst: 'Noe gikk galt. Prøv igjen.', feil: true });
          else if (erNettfeil(e)) setToast({ tekst: 'Ikke trukket — ingen nett. Prøv igjen når nettet er tilbake.', feil: true });
          else {
            setToast({ tekst: e.brukertekst, feil: true });
            // Noen andre kom først: hypotesen viser det som faktisk skjedde.
            friskOpp(h.id, h.number);
          }
        },
      },
    );
  };

  // j/k: neste og forrige av dine i «Mine hypoteser»s rekkefølge, som lista (DD-11). Aldri i et felt.
  const rekke = mineIRekkefolge(mine.data ?? []).filter((x) => x.number !== undefined);
  const rekkeRef = useRef(rekke);
  rekkeRef.current = rekke;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const maal = e.target as HTMLElement | null;
      if (maal && /^(INPUT|TEXTAREA|SELECT)$/.test(maal.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || (e.key !== 'j' && e.key !== 'k')) return;
      const r = rekkeRef.current;
      const i = r.findIndex((x) => x.number === nr);
      if (i === -1) return;
      const til = r[e.key === 'j' ? Math.min(r.length - 1, i + 1) : Math.max(0, i - 1)];
      if (til && til.number !== nr) void naviger(`/h/${til.number}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nr, naviger]);

  const h = hypotese.data;
  const lukkToast = useCallback(() => setToast(null), []);

  if (hypotese.isPending || me.isPending) {
    return (
      <Flate tittel={nummerTekst(nr)} tilbake={{ navn: 'Mine hypoteser', til: '/h/mine' }}>
        <div role="status" aria-busy="true" aria-label="Laster" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
        </div>
      </Flate>
    );
  }
  if (hypotese.isError || !h || h.submittedBy.id !== megId) {
    return (
      <Flate tittel={nummerTekst(nr)} tilbake={{ navn: 'Mine hypoteser', til: '/h/mine' }}>
        {/* K2: en hypotese som ikke finnes, sier det samme som en som ikke er din. */}
        <Varsel>{hypotese.isError && erNettfeil(hypotese.error) ? 'Fikk ikke hentet hypotesen. Sjekk nettet og prøv igjen.' : 'Fant ikke hypotesen. Den kan være slettet, eller ikke synlig for deg.'}</Varsel>
      </Flate>
    );
  }

  const cfg = config.data;
  // Overgang 14: mottatt, vurderes og parkert (kontrakten, `withdraw`). Mens kallet går, står valget ikke.
  const kanTrekke = (h.status === 'mottatt' || h.status === 'vurderes' || h.status === 'parkert') && !overgang.isPending;
  const svaret = h.status === 'konkludert' && h.conclusion?.kind === 'konklusjon';
  const svar = h.responses.filter((r) => r.kind !== 'trenger_mer').slice().reverse();
  const sporsmaal = h.awaitingSubmitter ? [...h.responses].reverse().find((r) => r.kind === 'trenger_mer') : undefined;

  return (
    <section className="hb-detalj hm-detalj" aria-labelledby="hm-tittel" data-testid="hm-detalj">
      {smal ? (
        <header className="h-stripe" data-testid="hm-stripe">
          <Link to="/h/mine" className="panel-stripe-tilbake" aria-label="Tilbake til Mine hypoteser">
            <Ikon navn="chevron-back-outline" />
          </Link>
          <span className="h-stripe-tittel">{nummerTekst(h.number)}</span>
        </header>
      ) : null}
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold hm-innhold">
          {smal ? null : (
            <>
              <Link to="/h/mine" className="panel-knapp-lenke hb-tilbake">
                <Ikon navn="chevron-back-outline" />
                Mine hypoteser
              </Link>
              <div className="hb-spor-topp">
                <span data-testid="hm-crumb">
                  {nummerTekst(h.number)}
                  {h.submittedAt ? ` · sendt ${kortDatoAv(h.submittedAt)}` : ''}
                </span>
                {rollerFor(h) ? (
                  <span className="hm-roller" data-testid="hm-roller">
                    {rollerFor(h)}
                  </span>
                ) : null}
              </div>
            </>
          )}
          {svaret ? (
            <SvaretErKommet h={h} config={cfg} />
          ) : (
            <>
              <div className="hm-hode">
                <h1 id="hm-tittel" className="hb-tittel hm-tittel">
                  {h.title}
                </h1>
                <NaaLinje h={h} config={cfg} />
              </div>
              <Sporingslinje ledd={sporFor(h, cfg, megId, false).map((l, i, alle) => (h.status === 'avvist' && i === alle.length - 1 ? { ...l, tone: 'avbrutt' as const } : l))} etikett="Hvor hypotesen står" />
              {sporsmaal ? <Sporsmaalskort h={h} sporsmaal={sporsmaal} laasId={h.decisionPending ? 'hm-laas' : undefined} /> : null}
              {/* H3.2 (W12a, W12c; P6, P10): låsen mens en avgjørelse venter — og etter en bekreftet
                  avvisning til begrunnelsen er skrevet. Setningen røper aldri utfallet eller hvem. */}
              {h.decisionPending ? <Laas h={h} medKnapp={!sporsmaal} /> : null}
              {/* Setningen står over 900, «fordi PC-en har plass til den» (W8); under er den ute (M5a, M5b). */}
              {h.sentence && !smal ? (
                <p className="hb-setning" data-testid="hm-setning">
                  {h.sentence}
                </p>
              ) : null}
            </>
          )}
          {svar.map((s) => {
            const kode = koden(h, s.transitionId, cfg);
            return (
              <div key={s.id} className="hb-svar" data-testid="hm-svar">
                <div className="hb-svar-hode">
                  <b>{`Svar fra ${navnAv(s.by)}${kode ? ` · ${kode}` : ''}`}</b>
                  <span>{kortDatoAv(s.at)}</span>
                </div>
                <p className="hb-tekst">{s.text}</p>
              </div>
            );
          })}
          <Logg h={h} config={cfg} megId={megId} />
          {kanTrekke ? (
            <button type="button" className="panel-knapp-lenke hm-trekk" onClick={() => setTrekker(true)} data-testid="hm-trekk">
              <Ikon navn={hypoteseOppforing('trukket').ikon} />
              Trekk hypotesen …
            </button>
          ) : null}
        </div>
      </div>
      {trekker ? (
        <TrekkDialog
          h={h}
          onAvbryt={() => setTrekker(false)}
          onTrekk={(note) => {
            setTrekker(false);
            trekk(h, note);
          }}
        />
      ) : null}
      {toast ? <Toast innhold={toast} onLukk={lukkToast} /> : null}
    </section>
  );
}

/** Crumbens høyre side (W8): hvem som har hypotesen nå. Mottatt har ingen ennå; konkludert står i svaret. */
function rollerFor(h: Hypothesis): string | null {
  const sist = [...h.transitions].reverse().find((t) => t.kind === 'overgang');
  switch (h.status) {
    case 'vurderes':
      return h.reviewer ? `${stor(navnAv(h.reviewer))} vurderer` : null;
    case 'parkert':
      return sist ? `${stor(navnAv(sist.by))} avgjør` : null;
    case 'avvist':
    case 'stoppet':
      return sist ? `${stor(navnAv(sist.by))} avgjorde` : null;
    case 'valgt':
    case 'testes':
    case 'pa_pause':
      return [h.owner ? `${stor(navnAv(h.owner))} eier` : null, h.decider ? `${navnAv(h.decider)} avgjør` : null].filter(Boolean).join(' · ') || null;
    default:
      return null;
  }
}

/** Avslags- eller stoppkoden som leietakerens ord, fra overgangen svaret hører til (DD-09). */
function koden(h: Hypothesis, overgangId: string | undefined, config: HypothesisConfig | undefined): string | undefined {
  const t = overgangId ? h.transitions.find((x) => x.id === overgangId) : undefined;
  if (t?.rejectReason) return config?.rejectReasonWording.find((w) => w.code === t.rejectReason)?.label;
  if (t?.stopReason) return config?.stopReasonWording.find((w) => w.code === t.stopReason)?.label;
  return undefined;
}

/**
 * Pillen, det som hører til den, og løftet (W8, M5a, M5b): pillen og løftet på samme linje når det
 * ikke er mer; med koden eller navnet ved pillen («For stor · Rune Aas · 14. okt») står løftet
 * under. Løftet er leietakerens ordlyd med plassholderne fylt ut.
 */
function NaaLinje({ h, config }: { h: Hypothesis; config: HypothesisConfig | undefined }) {
  const sist = [...h.transitions].reverse().find((t) => t.kind === 'overgang' && t.toStatus === h.status);
  const dato = kortDatoAv(sist?.at ?? h.statusChangedAt);
  const kode = h.status === 'avvist' ? koden(h, sist?.id, config) : h.status === 'stoppet' ? koden(h, sist?.id, config) : undefined;
  const ved = h.status === 'avvist' || h.status === 'parkert' || h.status === 'stoppet' || h.status === 'trukket' ? [kode, sist && h.status !== 'trukket' ? navnAv(sist.by) : null, dato].filter(Boolean).join(' · ') : null;
  const loefte = loefteFor(config, h);
  return (
    <div className={ved ? 'hm-naa hm-naa-under' : 'hm-naa'}>
      <span className="hb-naa-pille">
        <HypotesePille noekkel={hypoteseStatusNoekkel(h.status, h.outcome)} navn={statusNavn(config, h) || h.status} />
        {ved ? (
          <span className="hb-dempet" data-testid="hm-kode">
            {ved}
          </span>
        ) : null}
      </span>
      {loefte ? (
        <span className="hm-loefte" data-testid="hm-loefte">
          {loefte}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Spørsmålet etter «Trenger mer» (W8): ett kort med ÉN fylt knapp. Innsenderen svarer ved å endre
 * hypotesen — aldri ved å skrive tilbake (DD-27). «1 av 3» sier at det finnes en grense. I «Mine
 * hypoteser» (W7) bærer overskriften hypotesens tittel, fordi lista står under.
 */
export function Sporsmaalskort({ h, sporsmaal, medTittel = false, testid = 'hm-sporsmaal', laasId }: { h: Pick<Hypothesis, 'number' | 'title' | 'needsMoreCount'>; sporsmaal: Hypothesis['responses'][number]; medTittel?: boolean; testid?: string; /** H3.2: låsen står — knappen er sperret med grunnen i elementet med denne id-en. */ laasId?: string }) {
  const id = `${testid}-h`;
  return (
    <section className="hm-kort" aria-labelledby={id} data-testid={testid}>
      <div className="hm-kort-hode">
        <b id={id} className="hm-kort-tittel">
          <Ikon navn="help-circle-outline" className="hm-kort-ikon" />
          {`${stor(fornavn(sporsmaal.by))} lurer på én ting${medTittel && h.title ? ` · ${h.title}` : ''}`}
        </b>
        <span className="hb-liten" data-testid={`${testid}-tall`}>
          {medTittel ? kortDatoAv(sporsmaal.at) : `${kortDatoAv(sporsmaal.at)} · ${h.needsMoreCount} av ${MAKS_TRENGER_MER}`}
        </span>
      </div>
      <p className="hb-tekst">{sporsmaal.text}</p>
      <div>
        {/* «Endre hypotesen» (W8b) er en egen side: skjemaet med «Hva endret du?» (Endre.tsx). */}
        {laasId ? (
          <button type="button" className="panel-knapp hm-endre" aria-disabled="true" aria-describedby={laasId}>
            <Ikon navn="lock-closed-outline" />
            Endre hypotesen
          </button>
        ) : (
          <Link to={`/h/${h.number}/endre`} className="panel-knapp hm-endre">
            <Ikon navn="create-outline" />
            Endre hypotesen
          </Link>
        )}
      </div>
    </section>
  );
}

/**
 * Låsen (H3.2, W12a og W12c): «Endre hypotesen» er sperret fra «Start møtet» til avgjørelsen er
 * tatt — og etter en bekreftet avvisning til begrunnelsen er skrevet (P10). Setningen sier at
 * hypotesen er til avgjørelse og at fristen står — aldri hvem som bestemmer, aldri hva som ble
 * registrert (P6). Grunnen står i DOM-en ved knappen (`aria-describedby`), aldri bare i `title`.
 */
function Laas({ h, medKnapp }: { h: Hypothesis; medKnapp: boolean }) {
  const frist = h.deadlineAt ? ` — fristen ${langDatoAv(h.deadlineAt)} står` : '';
  return (
    <div className="hm-laas">
      <p id="hm-laas" className="h-grunn hm-laas-tekst" data-testid="hm-laas">
        <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
        <span>{`Hypotesen er til avgjørelse nå. Du kan endre den når avgjørelsen er tatt${frist}.`}</span>
      </p>
      {medKnapp ? (
        <button type="button" className="panel-knapp-sekundaer hm-endre" aria-disabled="true" aria-describedby="hm-laas">
          <Ikon navn="lock-closed-outline" />
          Endre hypotesen
        </button>
      ) : null}
    </div>
  );
}

/**
 * Konklusjonen som svar til innsenderen (M10c): datoen, «Svaret er kommet», og kortet med pillen og
 * nummeret, tittelen, de tre tallene etter størrelse og «Hva vi lærte». Ingen farge på tallet —
 * pillen sier utfallet med ord. Hele konklusjonen står i registeret.
 */
function SvaretErKommet({ h, config }: { h: Hypothesis; config: HypothesisConfig | undefined }) {
  const k = h.conclusion!;
  const naar = k.confirmedAt ?? h.statusChangedAt;
  const periode = testetPeriode(h);
  const linje = [periode ? `Testet ${periode}` : null, h.owner ? `${navnAv(h.owner)} eide` : null, k.confirmedBy ? `${navnAv(k.confirmedBy)} bekreftet` : null].filter(Boolean).join(' · ');
  return (
    <>
      <div className="hm-svaret-topp">
        <span className="hb-liten">{langDatoAv(naar)}</span>
        <h1 id="hm-tittel" className="hb-tittel">
          Svaret er kommet
        </h1>
      </div>
      <section className="hm-svaret" aria-labelledby="hm-svaret-tittel" data-testid="hm-svaret">
        <div className="hm-svaret-hode">
          <HypotesePille noekkel={hypoteseStatusNoekkel(h.status, h.outcome)} navn={statusNavn(config, h)} />
          <span className="hb-liten">{nummerTekst(h.number)}</span>
        </div>
        <p id="hm-svaret-tittel" className="hm-svaret-tittel">
          {h.title}
        </p>
        <Tallrekke h={h} etterStoerrelse />
        <div className="hm-laerte">
          <span className="hb-liten">Hva vi lærte</span>
          <p className="hb-tekst">{k.learned}</p>
        </div>
      </section>
      {linje ? (
        <p className="hb-dempet hm-testet" data-testid="hm-testet">
          {linje}
        </p>
      ) : null}
      <Link to={`/h/registeret/${h.number}`} className="panel-knapp-sekundaer hm-se-hele">
        Se hele konklusjonen
      </Link>
    </>
  );
}

/**
 * «Trekke H-142?» (`hypotese-innsending.jsx`, Trekk — ikke tegnet i konseptet): dialog, fordi det
 * rammer en annen person og ikke kan gjøres om etter kallet. Knappen er primær, aldri rød: hypotesen
 * blir stående som Trukket (DD-53). Grunnen er valgfri, ≤ 280 (`HypothesisWithdrawRequest.note`).
 */
function TrekkDialog({ h, onAvbryt, onTrekk }: { h: Hypothesis; onAvbryt: () => void; onTrekk: (note: string) => void }) {
  const [note, setNote] = useState('');
  const beskjed = h.reviewer ? ` ${stor(navnAv(h.reviewer))} får beskjed${h.status === 'vurderes' ? ', og fristen gjelder ikke lenger' : ''}.` : '';
  return (
    <Dialog tittelId="hm-trekk-h" onLukk={onAvbryt} testid="hm-trekk-dialog">
      <h2 id="hm-trekk-h" className="panel-h2">
        {`Trekke ${nummerTekst(h.number)}?`}
      </h2>
      <p className="panel-tekst">{`Den blir stående i registeret som Trukket, med alt som er skrevet.${beskjed}`}</p>
      <div className="h-felt">
        <label htmlFor="hm-trekk-note" className="h-etikett">
          Hvorfor? · valgfritt
        </label>
        <textarea id="hm-trekk-note" className="h-input h-tekstfelt" rows={3} maxLength={280} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="panel-handlinger">
        <Knapp label="Trekk hypotesen" primaer onClick={() => onTrekk(note.trim())} testid="hm-trekk-bekreft" />
        <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={onAvbryt} />
      </div>
    </Dialog>
  );
}
